import { Capacitor } from '@capacitor/core';
import {
  STRIPE_SUBSCRIPTION_PLAN_KEYS,
  type StripeSubscriptionPlanKey,
} from '@nfp-tracker/utils';

export type CheckoutState = StripeSubscriptionPlanKey | 'portal' | null;

export interface ResolvePlanActionArgs {
  isViewingUser: boolean;
  freeForLife: boolean;
  isInactiveStatus: boolean;
  hasStripeCustomer: boolean;
  selectedIsCurrentPlan: boolean;
  selectedPlan: StripeSubscriptionPlanKey;
  checkingOut: CheckoutState;
  onManageSubscription: (options?: { targetPlanKey?: StripeSubscriptionPlanKey }) => void;
  onCheckout: (planKey: StripeSubscriptionPlanKey) => void;
}

export interface PlanAction {
  label: string;
  onClick?: () => void;
  disabled: boolean;
  loading: boolean;
}

export interface OpenExternalUrlDependencies {
  isNativePlatform: () => boolean;
  getPlatform: () => string;
  openNativeExternalUrl: (url: string) => Promise<void>;
  windowOpen: (url: string, target?: string, features?: string) => Window | null;
  locationAssign: (url: string) => void;
}

export interface OpenExternalUrlOptions {
  allowWebSameTabFallback?: boolean;
}

const defaultOpenExternalUrlDependencies: OpenExternalUrlDependencies = {
  isNativePlatform: () => Capacitor.isNativePlatform(),
  getPlatform: () => Capacitor.getPlatform(),
  openNativeExternalUrl: async (url: string) => {
    const ExternalBrowser =
      typeof Capacitor.registerPlugin === 'function'
        ? Capacitor.registerPlugin<{ open(options: { url: string }): Promise<void> }>('ExternalBrowser')
        : null;

    if (!ExternalBrowser) {
      throw new Error('External browser plugin unavailable.');
    }

    await ExternalBrowser.open({ url });
  },
  windowOpen: (url: string, target?: string, features?: string) =>
    window.open(url, target, features),
  locationAssign: (url: string) => window.location.assign(url),
};

export const isCheckoutPlanKey = (value: string): value is StripeSubscriptionPlanKey =>
  value === STRIPE_SUBSCRIPTION_PLAN_KEYS.monthly ||
  value === STRIPE_SUBSCRIPTION_PLAN_KEYS.annual;

export const formatCurrencyFromMinorUnits = (
  amountInMinorUnits: number,
  currency: string
): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountInMinorUnits / 100);

export const openExternalUrl = (
  url: string,
  deps: OpenExternalUrlDependencies = defaultOpenExternalUrlDependencies,
  options: OpenExternalUrlOptions = {}
) => {
  const { allowWebSameTabFallback = true } = options;

  if (deps.isNativePlatform()) {
    if (deps.getPlatform() === 'ios') {
      void deps.openNativeExternalUrl(url).catch(() => {
        deps.locationAssign(url);
      });
      return;
    }

    deps.locationAssign(url);
    return;
  }

  const openedWindow = deps.windowOpen(url, '_blank', 'noopener,noreferrer');
  if (!openedWindow && allowWebSameTabFallback) {
    deps.locationAssign(url);
  }
};

export const resolvePlanAction = ({
  isViewingUser,
  freeForLife,
  isInactiveStatus,
  hasStripeCustomer,
  selectedIsCurrentPlan,
  selectedPlan,
  checkingOut,
  onManageSubscription,
  onCheckout,
}: ResolvePlanActionArgs): PlanAction => {
  if (isViewingUser) {
    return {
      label: 'Managed by your partner',
      onClick: undefined,
      disabled: true,
      loading: false,
    };
  }

  if (freeForLife) {
    return {
      label: 'Included with Free for Life',
      onClick: undefined,
      disabled: true,
      loading: false,
    };
  }

  if (isInactiveStatus) {
    if (hasStripeCustomer) {
      return {
        label: 'Restart Subscription',
        onClick: () => {
          onManageSubscription();
        },
        disabled: Boolean(checkingOut),
        loading: checkingOut === 'portal',
      };
    }

    return {
      label: 'Restart Subscription',
      onClick: () => {
        onCheckout(selectedPlan);
      },
      disabled: Boolean(checkingOut),
      loading: checkingOut === selectedPlan,
    };
  }

  if (selectedIsCurrentPlan) {
    return {
      label: 'Manage in Stripe',
      onClick: () => {
        onManageSubscription();
      },
      disabled: Boolean(checkingOut),
      loading: checkingOut === 'portal',
    };
  }

  return {
    label:
      selectedPlan === STRIPE_SUBSCRIPTION_PLAN_KEYS.annual
        ? 'Switch to Annual Billing'
        : 'Switch to Monthly Billing',
    onClick: () => {
      onManageSubscription({ targetPlanKey: selectedPlan });
    },
    disabled: Boolean(checkingOut),
    loading: checkingOut === 'portal',
  };
};
